import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { createTextColumn, date } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import type { RowDragState } from 'components/LazyRowContainer/LazyRowContainer';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useDraggableTable } from 'components/Table/model/useDraggableTable';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useLocale } from 'containers/App/LocaleContext';
import { isAfter, startOfDay } from 'date-fns';
import { useCallback, useMemo } from 'react';
import type { FC, JSX, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { renderNetworks } from './helpers/renderNetworks';

export type DeviceCredential = {
  /** list of CIDRs */
  excludeNetworks?: string[];
  expirationDate?: {
    enabled: boolean;
    // Null or undefined when enabled is false
    value?: string | null;
  };
  id: string;
  /** list of CIDRs */
  network?: string[];
  notes?: string | null;
  password?: string | null;
  passphrase?: string | null;
  priority: number;
  privateKey?: string | null;
  syslog: boolean;
  username: string;
  /**
   * Property telling backend to encrypt the submitted password. Populated automatically,
   * not used for an actual form field.
   */
  custom?: boolean;
};

type Props = {
  className?: string;
  passwords: DeviceCredential[];
  noCredentialBox: JSX.Element;
  onAddCredentialClick: () => void;
  onDeletePasswordClick: (id: string) => () => void;
  onDropPassword: (data: DeviceCredential[], from: number, to: number) => void;
  onEditPasswordClick: (password: DeviceCredential) => () => void;
  titleEndAdornment?: ReactNode;
};

type DeviceCredentialsTableRow = DeviceCredential & {
  allowedInSubnetsText: string;
  deniedInSubnetsText: string;
  expiresText: string;
  isExpired: boolean;
};

export const DevicePasswordsList: FC<Props> = (props) => {
  const credentialsMsg = useLocale().deviceCredentials.credentialsList;

  const globalCtxProps = useGlobalStateContextSelectors({
    dateFormat: prop('dateFormat'),
    timezone: prop('user', 'timezone'),
  });

  const tableData = useMemo<DeviceCredentialsTableRow[]>(
    () =>
      props.passwords.map((item) => {
        const isExpired = Boolean(
          item.expirationDate?.enabled &&
            item.expirationDate.value &&
            isAfter(
              startOfDay(new Date()),
              new Date(item.expirationDate.value),
            ),
        );
        const expiresText = isExpired
          ? 'Expired'
          : item.expirationDate?.enabled && item.expirationDate.value
            ? date.formatUtcTimestampInTimeZone(
                new Date(item.expirationDate.value),
                globalCtxProps.timezone,
                globalCtxProps.dateFormat,
              )
            : credentialsMsg.never;

        return {
          ...item,
          expiresText,
          isExpired,
          allowedInSubnetsText: item.network?.join(', ') ?? '',
          deniedInSubnetsText: item.excludeNetworks?.join(', ') ?? '',
        };
      }),
    [
      props.passwords,
      globalCtxProps.timezone,
      globalCtxProps.dateFormat,
      credentialsMsg.never,
    ],
  );

  const draggable = useDraggableTable({
    data: props.passwords,
    onDrop: props.onDropPassword,
    getRowIdentity: (row) => row.id,
  });

  const columns = useMemo(
    () => [
      createFrontendOnlyColumn<DeviceCredentialsTableRow>({
        key: 'priority',
        title: '',
        disabledSort: true,
        fixedWidth: true,
        width: 40,
        render: (item) => (
          <Icon
            glyph='fa-grip-dots-vertical'
            size='sm'
            className='cursor-grab'
            onMouseDown={() => {
              const index = props.passwords.findIndex((p) => p.id === item.id);
              if (index >= 0) {
                draggable.onRowDragStart(index);
              }
            }}
          />
        ),
      }),
      createTextColumn({
        key: 'username',
        title: 'Name',
        renderContent: ({ row }: { row: DeviceCredentialsTableRow }) => ({
          className: 'flex items-center',
          content: (
            <div className='flex-1'>
              <div className='text-sm'>{row.username}</div>
              {row.syslog && (
                <div className='text-xs'>
                  {credentialsMsg.usedIn}{' '}
                  <Link to='/settings/configuration-management'>
                    {credentialsMsg.configMgmt}
                  </Link>
                </div>
              )}
              {row.notes && (
                <div className='text-xs text-gray-800 mt-1'>{row.notes}</div>
              )}
            </div>
          ),
        }),
      }),
      createTextColumn({
        key: 'expiresText',
        title: credentialsMsg.expires,
        renderContent: ({ row }: { row: DeviceCredentialsTableRow }) => ({
          content: (
            <div className='flex-1'>
              {row.isExpired ? (
                <Badge color='warning' variant='plain' className='h-5 px-1.5'>
                  Expired
                </Badge>
              ) : (
                <div className='text-sm'>{row.expiresText}</div>
              )}
            </div>
          ),
        }),
      }),
      createTextColumn({
        key: 'allowedInSubnetsText',
        title: credentialsMsg.allowedInSubnets,
        renderContent: ({
          row,
          context,
          index,
        }: {
          row: DeviceCredentialsTableRow;
          context: TableCellContext;
          index: number;
        }) => ({
          className: 'flex items-center',
          content: (
            <div className='flex flex-wrap gap-1 text-sm'>
              {!row.network?.length
                ? credentialsMsg.noSubnets
                : renderNetworks(row.network, {
                    showAll: context.detailView,
                    onShowMore: () =>
                      context.onShowDetailClick?.(
                        index,
                        'allowedInSubnetsText',
                      ),
                  })}
            </div>
          ),
        }),
      }),
      createTextColumn({
        key: 'deniedInSubnetsText',
        title: credentialsMsg.deniedInSubnets,
        renderContent: ({
          row,
          context,
          index,
        }: {
          row: DeviceCredentialsTableRow;
          context: TableCellContext;
          index: number;
        }) => ({
          className: 'flex items-center',
          content: (
            <div className='flex flex-wrap gap-1 text-sm'>
              {!row.excludeNetworks?.length
                ? credentialsMsg.noSubnets
                : renderNetworks(row.excludeNetworks, {
                    showAll: context.detailView,
                    onShowMore: () =>
                      context.onShowDetailClick?.(index, 'deniedInSubnetsText'),
                  })}
            </div>
          ),
        }),
      }),
      createFrontendOnlyColumn<DeviceCredentialsTableRow>({
        key: 'actions',
        title: 'Actions',
        disabledSort: true,
        render: (item) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              dataTestId={`credentials-${item.username}-edit-icon`}
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={props.onEditPasswordClick(item)}
              type='button'
            />
            <TableButton
              dataTestId={`credentials-${item.username}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={props.onDeletePasswordClick(item.id)}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [
      credentialsMsg,
      props.onDeletePasswordClick,
      props.onEditPasswordClick,
      props.passwords,
      draggable.onRowDragStart,
    ],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: tableData,
  });
  const renderedRows = frontendSimpleFilter.data as DeviceCredentialsTableRow[];
  const sourceIndexById = useMemo(
    () =>
      new Map(props.passwords.map((row, index) => [row.id, index] as const)),
    [props.passwords],
  );
  const renderedIndexById = useMemo(
    () => new Map(renderedRows.map((row, index) => [row.id, index] as const)),
    [renderedRows],
  );

  const getRenderedDropBeforeIndex = useCallback(
    (dropBeforeIndex: number | null): number | null => {
      if (dropBeforeIndex === null) {
        return null;
      }
      if (dropBeforeIndex === props.passwords.length) {
        return renderedRows.length;
      }
      const beforeRowId = props.passwords[dropBeforeIndex]?.id;
      if (!beforeRowId) {
        return null;
      }
      return renderedIndexById.get(beforeRowId) ?? null;
    },
    [props.passwords, renderedRows.length, renderedIndexById],
  );

  const rowDragState = useMemo<RowDragState>(() => {
    const draggedRowId =
      draggable.rowDragState.draggedIndex === null
        ? null
        : (props.passwords[draggable.rowDragState.draggedIndex]?.id ?? null);
    return {
      draggedIndex:
        draggedRowId === null
          ? null
          : (renderedIndexById.get(draggedRowId) ?? null),
      dropBeforeIndex: getRenderedDropBeforeIndex(
        draggable.rowDragState.dropBeforeIndex,
      ),
    };
  }, [
    draggable.rowDragState.draggedIndex,
    draggable.rowDragState.dropBeforeIndex,
    getRenderedDropBeforeIndex,
    props.passwords,
    renderedIndexById,
  ]);

  const onRowMouseMove = useCallback(
    (renderedRowIndex: number, e: MouseEvent) => {
      const rowId = renderedRows[renderedRowIndex]?.id;
      if (!rowId) {
        return;
      }
      const sourceIndex = sourceIndexById.get(rowId);
      if (sourceIndex === undefined) {
        return;
      }
      draggable.onRowMouseMove(sourceIndex, e);
    },
    [draggable.onRowMouseMove, renderedRows, sourceIndexById],
  );

  const menuItems = useMemo(
    () => [
      {
        standalone: true,
        elementWithKey: (
          <Button
            dataTestId='device-credentials-add-device'
            icon='fa-circle-plus'
            key='add-new'
            onClick={props.onAddCredentialClick}
            size='small'
            variant='outline'
          >
            Add new
          </Button>
        ),
      } satisfies TableActionsItem,
    ],
    [props.onAddCredentialClick],
  );

  return (
    <Table
      borderless
      className={props.className}
      columns={columns}
      id='deviceCredentialsNewDiscovery'
      description='IP Fabric interacts with the network infrastructure devices by running
        show commands through CLI using SSH or Telnet. If more than one
        credential is specified, a top down algorithm is used when trying to
        login to a network device. You can easily change credentials priority
        using drag and drop.'
      menuItems={menuItems}
      rowIdField='id'
      rowHeight={70}
      title='Device Credentials'
      titleEndAdornment={props.titleEndAdornment}
      noDataBox={props.noCredentialBox}
      {...frontendSimpleFilter}
      rowDragState={rowDragState}
      onRowMouseMove={onRowMouseMove}
      onRowMouseLeave={draggable.onRowMouseLeave}
    />
  );
};
